import * as React from "react";
import { useRef } from "react";
import { toast } from "react-toastify";
import { Button } from "@progress/kendo-react-buttons";
import { SpdrClientViewModel } from "../../../../ViewModel/SpdrClientViewModel";
import { readVolumeDataFile, VolumeDataType } from "./ExtractVolumeDataFromBlob";
import {
  findPdsrClientViewModelByKey,
  PdsrClientViewModel,
} from "../../../../ViewModel/PdsrClientViewModel";
import { PdsrKey } from "../../../../ViewModel/PdsrKey";

function clearUploadInput(target: HTMLInputElement) {
  // eslint-disable-next-line no-param-reassign
  target.value = "";
}

function applyVolumeDataToSpdrs(
  spdrsOnThisRoute: Array<SpdrClientViewModel>,
  volumeData: Array<VolumeDataType>
) {
  volumeData.forEach((imported) => {
    const supplierGsdb = imported.Col1;
    const plantGsdb = imported.Col2;
    const dockCode = imported.Col3;
    const match = spdrsOnThisRoute.find(
      (x) => x.SupplierGsdb === supplierGsdb && x.PlantGsdb === plantGsdb && x.DockCode === dockCode
    );
    if (!match) {
      console.info(
        `Unable to find matching SPDR ${supplierGsdb}-${plantGsdb}-${dockCode}`,
        spdrsOnThisRoute
      );
      return;
    }

    match.InboundMegaLdm = imported.MLDM;
    match.InboundEuroLdm = imported.ELDM;
    match.InboundKg = imported.KG;
    match.InboundCublicMeters = imported.M3;
    match.ShipFrequency = imported.EngineeredSf;
  });
}

function applyVolumeDataToPdsrs(
  pdsrsOnThisRoute: Array<PdsrClientViewModel>,
  volumeData: Array<VolumeDataType>
) {
  volumeData.forEach((uploadedPdsr) => {
    const pdsrKey = PdsrKey.toPdsrKey(uploadedPdsr);

    const pdsrForKey = findPdsrClientViewModelByKey(pdsrsOnThisRoute, pdsrKey);

    if (!pdsrForKey) {
      console.info(
        `Unable to find matching PDSR ${pdsrKey.containerNetwork} ${pdsrKey.plantGsdb}-${pdsrKey.dockCode}-${pdsrKey.supplierGsdb}`,
        pdsrsOnThisRoute
      );
      return;
    }

    pdsrForKey.MegaLdm = uploadedPdsr.MLDM;
    pdsrForKey.EuroLdm = uploadedPdsr.ELDM;
    pdsrForKey.Weight = uploadedPdsr.KG;
    pdsrForKey.CubicMeters = uploadedPdsr.M3;
    pdsrForKey.ShipFrequency = uploadedPdsr.EngineeredSf;
  });
}

type Props = {
  spdrsOnThisRoute: Array<SpdrClientViewModel>;
  setSpdrsOnThisRoute: (spdrsOnThisRoute?: Array<SpdrClientViewModel>) => void;
  pdsrsOnThisRoute: Array<PdsrClientViewModel>;
  setPdsrsOnThisRoute: (pdsrsOnThisRoute?: Array<PdsrClientViewModel>) => void;
  isReadonly: boolean;
};

export function UploadVolumeData({
  spdrsOnThisRoute,
  setSpdrsOnThisRoute,
  pdsrsOnThisRoute,
  setPdsrsOnThisRoute,
  isReadonly,
}: Props) {
  const uploadControlRef = useRef<HTMLInputElement>(null);

  async function onUploadClick(event: React.FormEvent<HTMLInputElement>) {
    const target = event.target as HTMLInputElement;
    const { files } = target;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const volumeDataTypes = await readVolumeDataFile(file);
    clearUploadInput(target);

    const spdrsVolumeData = volumeDataTypes.filter((x) => !x.Network);
    applyVolumeDataToSpdrs(spdrsOnThisRoute, spdrsVolumeData);
    setSpdrsOnThisRoute(spdrsOnThisRoute);

    const pdsrsVolumeData = volumeDataTypes.filter((x) => x.Network);
    applyVolumeDataToPdsrs(pdsrsOnThisRoute, pdsrsVolumeData);
    setPdsrsOnThisRoute(pdsrsOnThisRoute);

    toast.success("Volume data has been updated!");
  }

  if (isReadonly) {
    return <div />;
  }

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label>
      <Button onClick={() => uploadControlRef.current?.click()}>Upload</Button>
      <input
        style={{ display: "none" }}
        type="file"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
        onInput={(event) => onUploadClick(event)}
        ref={uploadControlRef}
      />
    </label>
  );
}
